import React, { useMemo, useState } from 'react';
import Typography from '../StyledTypography';
import Box from 'apollo-react/components/Box';
import { convertUTCToLocal } from '../../utils/dateUtils';
import { getCommentAuthor } from './commentUtils';
import styles from './CommentViewer.module.css';
import itemStyles from './CommentItem.module.css';

import { useFilePreview } from '../../../hooks/api/files/useFilePreview';
import { useFileDownload } from '../../../hooks/api/files/useFileDownload';
import useCommentPermissions from '../../../hooks/features/comments/useCommentPermissions';
import useFieldsCommentsApi from '../../../hooks/api/comments/useFieldsCommentsApi';
import useAdditionalDocumentsCommentsApi from '../../../hooks/api/comments/useAdditionalDocumentsCommentsApi';
import useSubformCommentsApi from '../../../hooks/api/comments/useSubformCommentsApi';
import useAssessmentReviewApi from '../../../hooks/api/assessmentReview/useAssessmentReviewApi';
import Download from 'apollo-react-icons/Download';
import EyeShow2 from 'apollo-react-icons/EyeShow2';
import Attachment from 'apollo-react-icons/Paperclip';
import Pencil from 'apollo-react-icons/Pencil';
import Trash from 'apollo-react-icons/Trash';
import IconButton from 'apollo-react/components/IconButton';
import { toast } from 'react-toastify';
import Button from '../Button';
import EditCommentModal from './EditCommentModal/EditCommentModal';
import DeleteCommentDialog from './DeleteCommentDialog/DeleteCommentDialog';

/**
 * Normalize attachments from two possible sources:
 * - API-loaded comments: comment.attachments = [{ file: { id, original_name, ... } }]
 * - Local state (new request): comment.attachedFiles = [{ id, original_name, ... }]
 */
const normalizeAttachments = (comment) => {
  if (comment.attachments?.length > 0) {
    return comment.attachments;
  }
  if (comment.attachedFiles?.length > 0) {
    return comment.attachedFiles.map((file) => ({ file }));
  }
  return [];
};

const CommentItem = ({
  comment,
  t,
  compact = false,
  requestProgress,
  requestDateEnded,
  isAdditionalDocument = false,
  isSubformComment = false,
  isAssessmentReviewerComment = false,
  currentUserEmail,
  requestId,
  onCommentUpdated,
  onCommentDeleted,
}) => {
  const author = useMemo(() => getCommentAuthor(comment), [comment]);
  const attachments = useMemo(() => normalizeAttachments(comment), [comment]);
  const [downloadingFileId, setDownloadingFileId] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { canEdit, canDelete } = useCommentPermissions({
    comment,
    requestProgress,
    requestDateEnded,
    isAdditionalDocument,
    currentUserEmail,
  });

  const {
    updateComment: updateFieldComment,
    deleteComment: deleteFieldComment,
  } = useFieldsCommentsApi();
  const {
    updateComment: updateAdditionalDocComment,
    deleteComment: deleteAdditionalDocComment,
  } = useAdditionalDocumentsCommentsApi();
  const {
    updateComment: updateSubformComment,
    deleteComment: deleteSubformComment,
  } = useSubformCommentsApi();
  const {
    updateComment: updateAssessmentReviewComment,
    deleteComment: deleteAssessmentReviewComment,
  } = useAssessmentReviewApi();
  const updateComment = isAssessmentReviewerComment
    ? updateAssessmentReviewComment
    : isSubformComment
      ? updateSubformComment
      : isAdditionalDocument
        ? updateAdditionalDocComment
        : updateFieldComment;
  const deleteComment = isAssessmentReviewerComment
    ? deleteAssessmentReviewComment
    : isSubformComment
      ? deleteSubformComment
      : isAdditionalDocument
        ? deleteAdditionalDocComment
        : deleteFieldComment;

  const isEdited =
    comment.updatedAt &&
    comment.createdAt &&
    new Date(comment.updatedAt).getTime() !==
      new Date(comment.createdAt).getTime();

  const displayRole =
    author.role || (author.isEndUser ? t('end_user') : t('reviewer'));

  const { canPreviewFile, previewFile } = useFilePreview();
  const { downloadFile, error } = useFileDownload();

  const handleDownload = async (fileId, fileName) => {
    setDownloadingFileId(fileId);
    await downloadFile(fileId, fileName || `attachment_${fileId}`);
    setDownloadingFileId(null);
    if (error) {
      toast.error(error, { position: 'top-center' });
    }
  };

  const handleUpdateComment = async (data) => {
    const result = await updateComment(data);
    if (onCommentUpdated) {
      onCommentUpdated(result);
    }
    return result;
  };

  const handleDeleteComment = async (data) => {
    const result = await deleteComment(data);
    if (onCommentDeleted) {
      onCommentDeleted(comment.id);
    }
    return result;
  };

  const showActions = canEdit || canDelete;

  return (
    <Box
      className={`${styles.commentItem} ${compact ? styles.commentItemCompact : ''}`}
    >
      <Box className={styles.commentHeader}>
        <Box className={styles.userInfo}>
          <Typography className={styles.username}>
            {author.name || t('unknown_user')}
          </Typography>
          <Typography className={styles.role}>{displayRole}</Typography>
        </Box>
        <Box className={itemStyles.headerRight}>
          <Typography className={styles.timestamp}>
            {convertUTCToLocal(comment.createdAt)}
          </Typography>
          {isEdited && (
            <Typography className={itemStyles.editedLabel}>
              {t('edited')}
            </Typography>
          )}
        </Box>
      </Box>
      {!compact && author.email && (
        <Box className={styles.emailContainer}>
          <Typography variant="bodySmall" className={styles.email}>
            {author.email}
          </Typography>
        </Box>
      )}
      <Box className={styles.commentBox}>
        <Typography variant="bodyDefault" className={styles.commentText}>
          {comment.comment}
        </Typography>
      </Box>
      {showActions && (
        <Box className={itemStyles.actionIcons}>
          {canDelete && (
            <IconButton
              size="small"
              onClick={() => setDeleteDialogOpen(true)}
              className={itemStyles.actionButton}
            >
              <Trash size="small" />
            </IconButton>
          )}
          {canDelete && canEdit && <Box className={itemStyles.actionDivider} />}
          {canEdit && (
            <IconButton
              size="small"
              onClick={() => setEditModalOpen(true)}
              className={itemStyles.actionButton}
            >
              <Pencil size="small" />
            </IconButton>
          )}
        </Box>
      )}

      {/* attachments */}
      {attachments.length > 0 &&
        attachments.map(({ file }) => {
          const isPreviewable = canPreviewFile(file?.mime_type || '');
          return (
            <Box key={file.id} className={itemStyles.attachmentRow}>
              <Box className={itemStyles.attachmentIconWrapper}>
                <Attachment
                  color="#666"
                  className={itemStyles.attachmentIcon}
                />
              </Box>
              <Typography
                variant="bodySmall"
                className={itemStyles.attachmentName}
              >
                {file?.original_name || file?.name || `attachment_${file.id}`}
              </Typography>

              <Box className={itemStyles.attachmentActions}>
                <Button
                  disabled={!isPreviewable}
                  color="secondary"
                  size="small"
                  endIcon={<EyeShow2 size={11} />}
                  onClick={() =>
                    previewFile(file.id, file?.original_name || file?.name)
                  }
                  className={itemStyles.previewButton}
                  text={t('preview')}
                />

                <Button
                  color="secondary"
                  size="small"
                  endIcon={<Download size={11} />}
                  onClick={() =>
                    handleDownload(file.id, file?.original_name || file?.name)
                  }
                  disabled={downloadingFileId === file.id}
                  className={itemStyles.downloadButton}
                  text={
                    downloadingFileId === file.id
                      ? t('downloading')
                      : t('download')
                  }
                />
              </Box>
            </Box>
          );
        })}

      {/* Edit Modal */}
      <EditCommentModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        comment={comment}
        onUpdateComment={handleUpdateComment}
        requestId={requestId}
        isAdditionalDocument={isAdditionalDocument}
      />

      {/* Delete Dialog */}
      <DeleteCommentDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        commentId={comment.id}
        onDeleteComment={handleDeleteComment}
        requestId={requestId}
        isAdditionalDocument={isAdditionalDocument}
      />
    </Box>
  );
};

export default CommentItem;
